import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import AddCamera from "./pages/AddCamera";
import ViewCameras from "./pages/ViewCameras";
import AskQuestion from "./components/AskQuestion";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HistoricalLogs from "./pages/HistoricalLogs";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import OrganizationSettings from "./pages/OrganizationSettings";
import InvitationManagement from "./pages/InvitationManagement";
import UserManagement from "./pages/UserManagement";
import UserProfile from "./pages/UserProfile";
import PermissionGuard from "./components/PermissionGuard";
import RequireAuth from "./components/RequireAuth";
import { notifyAuthDataUpdated } from "./utils/authUtils";

function App() {
  const location = useLocation();
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Load auth data from URL params (passed from white-tiger-landing on login/signup)
  // IMPORTANT: This runs ONCE on mount to prevent race condition with Sidebar API calls
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const refreshToken = params.get('refreshToken');
    const organizationId = params.get('organizationId');
    const userRoles = params.get('userRoles');

    if (token) {
      console.log('[App] Loading auth data from URL params');

      // Save to localStorage IMMEDIATELY to prevent 401 errors
      localStorage.setItem('accessToken', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      if (organizationId) localStorage.setItem('organizationId', organizationId);
      if (userRoles) localStorage.setItem('userRoles', userRoles);

      console.log('[App] Auth data saved to localStorage:', {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        organizationId,
        userRoles
      });

      notifyAuthDataUpdated({ invalidateMetadata: true });

      // Clean URL (remove query params for security)
      const cleanPath = location.pathname;
      window.history.replaceState({}, '', cleanPath);

      // Don't navigate, just mark auth as ready
      console.log('[App] Auth data ready, rendering app');
    }

    // Mark auth as ready (either token was extracted or no token in URL)
    setIsAuthReady(true);
  }, []); // Empty dependency array - run ONCE on mount

  // Show loading state until auth data is extracted
  if (!isAuthReady) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Protected Routes - Require Authentication */}
        <Route path="/" element={<RequireAuth><AddCamera /></RequireAuth>} />
        <Route path="/view" element={<RequireAuth><ViewCameras /></RequireAuth>} />
        <Route path="/ask" element={<RequireAuth><AskQuestion /></RequireAuth>} />
        <Route path="/logs" element={<RequireAuth><HistoricalLogs /></RequireAuth>} />
        <Route path="/analytics" element={<RequireAuth><AnalyticsDashboard /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><UserProfile /></RequireAuth>} />

        {/* Admin Routes - Require Authentication + Admin Role */}
        <Route
          path="/admin/organization"
          element={
            <RequireAuth>
              <PermissionGuard requiredRole="Admin">
                <OrganizationSettings />
              </PermissionGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/invitations"
          element={
            <RequireAuth>
              <PermissionGuard requiredRole="Admin">
                <InvitationManagement />
              </PermissionGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth>
              <PermissionGuard requiredRole="Admin">
                <UserManagement />
              </PermissionGuard>
            </RequireAuth>
          }
        />
      </Routes>

      {/* one ToastContainer for the whole app */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
      />
    </>
  );
}

export default App;
